import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Typography
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin,
  TaskContext
} from '@sierralabs/workflow-ui';

import JiraIssueTypePicker from '../../../jira-connect/pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../../../jira-connect/pickers/JiraProjectPicker';
import { useJiraConnectContext } from '../../../jira-connect/JiraConnect.context';
import {
  useCreateJiraIssue,
  useFindJiraIssues
} from '../../../jira-connect/JiraConnect.hooks';
import {
  JiraIssue,
  JiraIssueCreateResponse
} from '../../../jira-connect/JiraConnect.interface';
import { useCreateTaskJiraIssueV2 } from '../../../task-jira-v2/TaskJiraIssueV2.hooks';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .MuiFormControlLabel-root {
    width: 100%;
  }
`;

export interface CreateJiraIssueParams {
  src: 'createJiraIssue';
  data: {
    projectId?: string;
    issueTypeId?: string;
    linkToEntity?: boolean;
    duplicateTitle?: boolean;
    duplicateDescription?: boolean;
  };
}

const CreateJiraIssueConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: CreateJiraIssueParams['data'] = invoke.data;
  return (
    <StyledConfiguratorDiv>
      <JiraProjectPicker
        value={data.projectId}
        textFieldProps={{ size: 'small' }}
        onChange={projectId => {
          data.projectId = projectId;
          data.issueTypeId = undefined;
          const updatedInvoke = { ...invoke, data };
          setInvoke(updatedInvoke);
          onChange?.(updatedInvoke);
        }}
      />
      <JiraIssueTypePicker
        value={data.issueTypeId}
        projectId={data.projectId}
        disabled={!data.projectId}
        textFieldProps={{ size: 'small' }}
        onChange={issueType => {
          data.issueTypeId = issueType?.id;
          const updatedInvoke = { ...invoke, data };
          setInvoke(updatedInvoke);
          onChange?.(updatedInvoke);
        }}
      />

      <FormControlLabel
        label='Link to Current QMS Entity'
        control={
          <Checkbox
            color='primary'
            checked={data.linkToEntity || false}
            onChange={(_, checked) => {
              data.linkToEntity = checked;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        }
      />
      <FormControlLabel
        label='Duplicate Title Text'
        control={
          <Checkbox
            color='primary'
            checked={data.duplicateTitle || false}
            onChange={(_, checked) => {
              data.duplicateTitle = checked;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        }
      />
      <FormControlLabel
        label='Duplicate Description Text'
        control={
          <Checkbox
            color='primary'
            checked={data.duplicateDescription || false}
            onChange={(_, checked) => {
              data.duplicateDescription = checked;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        }
      />
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  padding-left: 4px;
  text-indent: -4px;
  code {
    background: #f4f5fa;
    padding: 2px 5px;
    border-radius: 3px;
  }
  b {
    padding: 0 5px 0 0;
  }
`;

const CreateJiraIssueDisplay: React.FC<ServiceDisplayProps> = ({ invoke }) => {
  return (
    <StyledDisplayDiv>
      {invoke.data?.events?.map((event: any, index: number) => (
        <div className='line' key={index}>
          {event.description || event.event}
        </div>
      ))}
    </StyledDisplayDiv>
  );
};

const StyledDialogContent = styled(DialogContent)`
  .MuiTypography-root,
  .MuiAlert-root {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

interface CreateJiraIssuePluginDialogProps {
  open: boolean;
  state: State<TaskContext>;
  data: CreateJiraIssueParams['data'];
  onCancel?: () => void;
  onSuccess?: () => void;
}

const CreateJiraIssuePluginDialog: React.FC<
  CreateJiraIssuePluginDialogProps
> = props => {
  const { open, data, state, onCancel, onSuccess } = props;
  const stateRef = useRef(state);
  const onSuccessRef = useRef(onSuccess);
  const [issue, setIssue] = useState<JiraIssue>();
  const [result, setResult] = useState<JiraIssueCreateResponse>();
  const { isAvailable } = useJiraConnectContext();
  const [foundResult, findIssues] = useFindJiraIssues();
  const [createResult, createIssue] = useCreateJiraIssue();
  const {
    data: createdTaskJiraIssue,
    isLoading: isCreating,
    error: createError,
    createTaskJiraIssue
  } = useCreateTaskJiraIssueV2();
  const { projectId, issueTypeId, linkToEntity } = data;
  const createdIssues = foundResult.data;
  const isLoading =
    createResult.isLoading || foundResult.isLoading || isCreating;
  const error = createResult.error || foundResult.error || createError;

  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    if (!open) {
      setIssue(undefined);
      setResult(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!isAvailable || !projectId || !issueTypeId) {
      return;
    }
    const { task, taskEntity } = stateRef.current.context;
    createIssue({
      fields: {
        summary: task.name,
        description: (taskEntity as any)?.description || task.description,
        project: { id: projectId },
        issuetype: { id: issueTypeId }
      }
    });
  }, [projectId, issueTypeId, isAvailable, createIssue]);

  useEffect(() => {
    setResult(createResult.data);
  }, [createResult.data]);

  useEffect(() => {
    if (result) {
      findIssues({
        title: result.key,
        data: { reconcileIssues: [Number(result.id)] }
      });
    }
  }, [result, findIssues]);

  useEffect(() => {
    const issue = createdIssues?.data[0];
    issue && setIssue(issue);
  }, [createdIssues]);

  useEffect(() => {
    if (!issue) {
      return;
    }
    if (!linkToEntity) {
      onSuccessRef.current?.();
      return;
    }
    // link issue
    createTaskJiraIssue({
      taskId: stateRef.current.context.task.id,
      issue,
      key: issue.key,
      summary: issue.fields.summary,
      status: issue.fields.status.name
    });
  }, [linkToEntity, issue, createTaskJiraIssue]);

  useEffect(() => {
    if (createdTaskJiraIssue) {
      onSuccessRef.current?.();
    }
  }, [createdTaskJiraIssue]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        // avoid accidentally cancelling create jira request
        if (!isLoading) {
          onCancel?.();
        }
      }}
    >
      {isLoading && <LinearProgress />}
      <DialogTitle>Create Jira Issue</DialogTitle>
      <StyledDialogContent>
        {isLoading && (
          <Typography>
            {createResult.isLoading
              ? 'Creating Jira Issue...'
              : foundResult.isLoading
              ? `Loading created Jira Issue ${result?.key || '?'}...`
              : isCreating
              ? `Linking Jira Issue ${result?.key || '?'}...`
              : 'Loading...'}
          </Typography>
        )}
        {!isAvailable && (
          <Alert severity='warning'>
            <AlertTitle>JIRA Connect API is not available</AlertTitle>
            Unable to run action and create Jira issue. You can choose to skip
            this action, or open SierraQMS within JIRA and try again to properly
            run this action.
          </Alert>
        )}
        {error && <Alert severity='error'>{error.message}</Alert>}
      </StyledDialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={onCancel}>
          Cancel
        </Button>
        {!isAvailable && (
          <Button
            color='primary'
            variant='outlined'
            disabled={isLoading}
            onClick={() => onSuccess?.()}
          >
            Skip Action and Proceed
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const CreateJiraIssuePlugin: ServicePlugin<CreateJiraIssueParams> = {
  src: 'createJiraIssue',
  name: 'Create Jira Issue',
  configurator: CreateJiraIssueConfigurator,
  display: CreateJiraIssueDisplay,
  validate: (condition: Partial<WorkflowInvoke>) => {
    const data = condition.data as CreateJiraIssueParams['data'];
    return !!(data && data.projectId && data.issueTypeId);
  },
  service: (
    state: State<TaskContext>,
    params: CreateJiraIssueParams | CreateJiraIssueParams[],
    render
  ) => {
    console.log('createJiraIssue params', state, params);
    const pluginParams = Array.isArray(params) ? params[0] : params;
    return new Promise((resolve, reject) => {
      render?.(
        <CreateJiraIssuePluginDialog
          open
          state={state}
          data={pluginParams.data}
          onCancel={() => {
            render?.(undefined);
            reject();
          }}
          onSuccess={() => {
            render?.(undefined);
            resolve(true);
          }}
        />
      );
    });
  }
};
